import React, { useEffect } from 'react';
import type { FC } from 'react';
import type { DevMetadata } from '@wix/cli-astro-definitions';
import { Spinner } from '@wix/cli-ui-kit';
import { isType, match } from 'variant';
import { useProjectModel } from '../../model.js';
import type { ProjectModel } from '../../model.js';
import { useAstroLogger } from './utils/useAstroLogger.js';
import { useFetchDevMetadata } from './hooks/useFetchDevMetadata.js';
import { DevScreenBorder } from './DevScreenBorder.js';
import { SiteDevFlow } from './SiteDevFlow.js';
import { AppDevFlow } from './AppDevFlow.js';
import { useDevServerLocalUrl } from './hooks/useDevServerLocalUrl.js';

interface DevCommandProps {
  port?: number;
  allowedHosts?: string;
  serverExposedUrl?: string;
  base?: string;
}

export const DevCommand: FC<DevCommandProps> = ({
  port,
  allowedHosts,
  serverExposedUrl,
  base,
}) => {
  const { model } = useProjectModel();

  const logger = useAstroLogger({ isPrefixed: false });
  const fetchDevMetadataAction = useFetchDevMetadata({ base });

  const devServerStatus = useDevServerLocalUrl({
    model,
    base,
    port,
    allowedHosts,
    logger,
  });

  const serverLocalUrl = isType(devServerStatus, 'Success')
    ? devServerStatus.result
    : undefined;

  useEffect(() => {
    if (serverLocalUrl) {
      void fetchDevMetadataAction.execute(serverLocalUrl);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLocalUrl]);

  if (isType(devServerStatus, 'Error')) {
    return null;
  }

  if (!serverLocalUrl) {
    return <Spinner text="Starting dev environment..." />;
  }

  return match(fetchDevMetadataAction.status, {
    NotRequested: () => <Spinner text="Starting dev environment..." />,
    Error: () => null,
    Loading: () => <Spinner text="Checking dev environment settings..." />,
    Success: ({ result: { devMetadata, serverLocalUrl } }) => (
      <DevFlowScreen
        model={model}
        base={base}
        serverLocalUrl={serverLocalUrl}
        serverExposedUrl={serverExposedUrl}
        devMetadata={devMetadata}
      />
    ),
  });
};

const DevFlowScreen: FC<{
  model: ProjectModel;
  base?: string;
  serverLocalUrl: string;
  serverExposedUrl?: string;
  devMetadata: DevMetadata | null;
}> = ({ model, base, serverLocalUrl, serverExposedUrl, devMetadata }) => (
  <DevScreenBorder>
    {model.config.projectType === 'Site' ? (
      <SiteDevFlow
        serverLocalUrl={serverLocalUrl}
        serverExposedUrl={serverExposedUrl}
        devMetadata={devMetadata}
      />
    ) : (
      <AppDevFlow
        base={base}
        serverLocalUrl={serverLocalUrl}
        serverExposedUrl={serverExposedUrl}
        devMetadata={devMetadata}
      />
    )}
  </DevScreenBorder>
);
